import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock, Star, ThumbsUp, ThumbsDown, Lightbulb } from 'lucide-react';
import { questionnaireAPI } from '../../services/api';
import { format } from 'date-fns';

const QuestionnaireForm = () => {
  const { questionnaireId } = useParams();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [responses, setResponses] = useState({});
  const [vibeCheckAnswer, setVibeCheckAnswer] = useState(null);
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [loadingActionItems, setLoadingActionItems] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showActionItems, setShowActionItems] = useState(false);
  const [error, setError] = useState(null);

  const PILLARS = [
    'We Own Safety',
    'We Own Quality',
    'We Own the Workplace',
    'We Develop People',
    'We Drive Flow',
    'We Drive Value',
    'We Seek Perfection'
  ];

  useEffect(() => {
    loadQuestionnaire();
  }, [questionnaireId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadQuestionnaire = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await questionnaireAPI.getQuestionnaire(questionnaireId);
      setQuestionnaire(data);
      
      // Initialize responses object
      const initialResponses = {};
      data.questions.forEach((_, index) => {
        initialResponses[index] = null;
      });
      setResponses(initialResponses);
      
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      setError(error.message || 'Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const handleVibeCheckChange = (answer) => {
    setVibeCheckAnswer(answer);
    setResponses(prev => ({ ...prev, 0: answer }));
    
    if (answer === 'YES') {
      setSelectedPillar(null);
      setActionItems([]);
    }
  };

  const handlePillarSelect = (pillar) => {
    setSelectedPillar(pillar);
  };

  const generateActionItems = async (pillar) => {
    setLoadingActionItems(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GPS_API_URL}/api/vibe-check/action-items`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pillar_name: pillar })
        }
      );
      
      if (!response.ok) throw new Error('Failed to generate action items');
      
      const data = await response.json();
      setActionItems(data.action_items || []);
    } catch (error) {
      console.error('Failed to load action items:', error);
      setActionItems([
        'Talk to your supervisor about how to improve in this area',
        'Review the GPS principles posted in your work area',
        'Ask a coworker for tips on improving in this area'
      ]);
    } finally {
      setLoadingActionItems(false);
    }
  };

  const handleResponseChange = (questionIndex, rating) => {
    setResponses(prev => ({
      ...prev,
      [questionIndex]: rating
    }));
  };

  const handleSubmit = async () => {
    const isVibeCheck = questionnaire?.questions?.[0]?.question_type === 'VIBE_CHECK';

    if (isVibeCheck) {
      // Validate vibe check questionnaire
      if (vibeCheckAnswer === null) {
        setError('Please answer the vibe check question.');
        return;
      }
      if (vibeCheckAnswer === 'NO' && !selectedPillar) {
        setError('Please select where you need help.');
        return;
      }
      if (responses[1] === null) {
        setError('Please answer the pillar question.');
        return;
      }
    } else {
      // Validate old format questionnaire
      const unansweredQuestions = Object.values(responses).filter(response => response === null);
      if (unansweredQuestions.length > 0) {
        setError('Please answer all questions before submitting.');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      let responseData;
      if (isVibeCheck) {
        responseData = {
          vibe_check: vibeCheckAnswer,
          selected_pillar: selectedPillar,
          pillar_rating: responses[1],
          action_items: vibeCheckAnswer === 'NO' ? actionItems : []
        };
      } else {
        responseData = questionnaire.questions.map((question, index) => ({
          question_index: index,
          question_text: question.text,
          rating: responses[index]
        }));
      }

      await questionnaireAPI.submitResponse(questionnaireId, responseData);
      setSubmitted(true);
      
      // Generate action items after submission if needed
      if (isVibeCheck && vibeCheckAnswer === 'NO' && selectedPillar) {
        setShowActionItems(true);
        await generateActionItems(selectedPillar);
      }
      
    } catch (error) {
      console.error('Failed to submit responses:', error);
      setError(error.message || 'Failed to submit responses');
    } finally {
      setSubmitting(false);
    }
  };

  const getCompletionPercentage = () => {
    const isVibeCheck = questionnaire?.questions?.[0]?.question_type === 'VIBE_CHECK';
    
    if (isVibeCheck) {
      let completed = 0;
      let total = 2;
      
      if (vibeCheckAnswer) completed++;
      if (vibeCheckAnswer === 'NO' && selectedPillar) completed += 0.5;
      if (responses[1]) completed++;
      
      return Math.round((completed / total) * 100);
    } else {
      const totalQuestions = questionnaire?.questions?.length || 0;
      const answeredQuestions = Object.values(responses).filter(response => response !== null).length;
      return totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;
    }
  };

  if (loading) {
    return (
      <div className="questionnaire-container flex items-center justify-center p-4">
        <div className="questionnaire-card w-full max-w-md text-center">
          <div className="spinner mb-6"></div>
          <p className="loading-text">Loading questionnaire...</p>
        </div>
      </div>
    );
  }

  if (error && !questionnaire) {
    return (
      <div className="questionnaire-container flex items-center justify-center p-4">
        <div className="questionnaire-card w-full max-w-md text-center">
          <AlertCircle className="mx-auto h-12 w-12 mb-4" style={{ color: '#dc2626' }} />
          <h2 className="text-lg font-semibold mb-2" style={{ color: '#111827' }}>Unable to Load Questionnaire</h2>
          <p className="mb-4" style={{ color: '#6b7280' }}>{error}</p>
          <button className="questionnaire-button questionnaire-button-secondary" onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="questionnaire-container" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container" style={{ maxWidth: '600px', margin: '0 auto', padding: '24px' }}>
          {/* Success Card */}
          <div style={{ 
            background: 'white', 
            borderRadius: '24px', 
            padding: '32px 24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center',
            marginBottom: showActionItems ? '24px' : '0'
          }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircle size={48} color="white" strokeWidth={3} />
            </div>
            
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#111827',
              marginBottom: '12px'
            }}>
              Thank You!
            </h2>
            
            <p style={{ 
              fontSize: '16px', 
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              Your feedback has been submitted successfully. Your input helps us improve every day.
            </p>
            
            <div style={{ 
              background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
              borderRadius: '16px',
              padding: '16px',
              border: '2px solid #10b981'
            }}>
              <p style={{ fontSize: '14px', color: '#059669', lineHeight: '1.8' }}>
                <strong>{questionnaire.benchmark_area}</strong><br />
                {questionnaire.site_name} • {questionnaire.year}
              </p>
            </div>
          </div>

          {/* Action Items Card */}
          {showActionItems && (
            <div style={{ 
              background: 'white', 
              borderRadius: '24px', 
              padding: '32px 24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <Lightbulb size={36} color="white" strokeWidth={2.5} />
                </div>
                
                <h3 style={{ 
                  fontSize: '22px', 
                  fontWeight: '700', 
                  color: '#111827',
                  marginBottom: '8px'
                }}>
                  Here's How You Can Improve
                </h3>
                
                <p style={{ 
                  fontSize: '14px', 
                  color: '#6b7280',
                  marginBottom: '0'
                }}>
                  3 things you can do this week for <strong>{selectedPillar}</strong>
                </p>
              </div>

              {loadingActionItems ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div className="spinner" style={{ margin: '0 auto' }}></div>
                  <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '14px' }}>
                    Generating personalized action items...
                  </p>
                </div>
              ) : (
                <div style={{ marginTop: '24px' }}>
                  {actionItems.map((item, index) => (
                    <div key={index} style={{ 
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      borderRadius: '16px',
                      padding: '20px',
                      marginBottom: '16px',
                      border: '2px solid #f59e0b',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px'
                    }}>
                      <div style={{ 
                        minWidth: '32px',
                        height: '32px',
                        background: '#f59e0b',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '16px'
                      }}>
                        {index + 1}
                      </div>
                      <p style={{ 
                        color: '#92400e',
                        fontSize: '15px',
                        lineHeight: '1.6',
                        margin: '4px 0 0 0',
                        flex: 1
                      }}>
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              <div style={{ 
                marginTop: '24px',
                padding: '16px',
                background: '#f3f4f6',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <p style={{ 
                  fontSize: '13px', 
                  color: '#6b7280',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  💡 <strong>Tip:</strong> Pick one action to focus on this week. Small steps lead to big improvements!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-details-container">
      {/* Header */}
      <div className="assessment-header">
        <div className="container">
          <button 
            className="assessment-back-button" 
            onClick={() => window.location.href = '/'}
            style={{ marginBottom: '1.5rem' }}
          >
            ← Back to Dashboard
          </button>
          <h1 className="assessment-title">
            {questionnaire.benchmark_area}
          </h1>
          <p className="assessment-subtitle">
            {questionnaire.site_name} • {questionnaire.year}
          </p>
          
          <div className="flex items-center justify-center gap-8 mt-8">
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ color: '#10b981' }}>
                {getCompletionPercentage()}%
              </div>
              <div className="text-base" style={{ color: '#6b7280' }}>Complete</div>
            </div>
            
            <div className="flex-1 max-w-md">
              <div className="w-full rounded-full h-3" style={{ background: '#e5e7eb' }}>
                <div 
                  className="h-3 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${getCompletionPercentage()}%`,
                    background: 'linear-gradient(90deg, #6366f1 0%, #10b981 100%)'
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="assessment-content">
        {/* Expiration Notice */}
        {questionnaire.expiration_date && (
          <div className="stats-card mb-8" style={{ background: '#fef3c7', border: '1px solid #d97706' }}>
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-3" style={{ color: '#d97706' }} />
              <div>
                <p className="text-base font-medium" style={{ color: '#92400e' }}>
                  Questionnaire expires on {format(new Date(questionnaire.expiration_date), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="stats-card mb-8" style={{ background: '#fee2e2', border: '1px solid #dc2626' }}>
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-3" style={{ color: '#dc2626' }} />
              <p className="text-base" style={{ color: '#dc2626' }}>{error}</p>
            </div>
          </div>
        )}

        {/* Progress Summary - Show current selections */}
        {questionnaire.questions[0]?.question_type === 'VIBE_CHECK' && (vibeCheckAnswer || responses[1]) && (
          <div className="stats-card mb-8" style={{ 
            background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)', 
            border: '2px solid #8b5cf6',
            padding: '20px'
          }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '700', 
              color: '#6b21a8',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <CheckCircle size={20} />
              Your Progress - You can change any answer before submitting
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {vibeCheckAnswer && (
                <div style={{ 
                  background: 'white',
                  padding: '12px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ color: '#374151', fontSize: '14px' }}>
                    <strong>Question 1:</strong> Feeling good? <strong>{vibeCheckAnswer}</strong>
                  </span>
                  {vibeCheckAnswer === 'NO' && selectedPillar && (
                    <span style={{ 
                      color: '#f59e0b', 
                      fontSize: '13px',
                      fontWeight: '600',
                      background: '#fef3c7',
                      padding: '4px 12px',
                      borderRadius: '12px'
                    }}>
                      Need help: {selectedPillar}
                    </span>
                  )}
                </div>
              )}
              {responses[1] && (
                <div style={{ 
                  background: 'white',
                  padding: '12px',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#374151', fontSize: '14px' }}>
                    <strong>Question 2:</strong> Rated {responses[1]} stars
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-8">
          {questionnaire.questions[0]?.question_type === 'VIBE_CHECK' ? (
            <>
              {/* Vibe Check Format */}
              <VibeCheckQuestion
                question={questionnaire.questions[0]}
                vibeCheckAnswer={vibeCheckAnswer}
                onAnswerChange={handleVibeCheckChange}
                selectedPillar={selectedPillar}
                onPillarSelect={handlePillarSelect}
                pillars={PILLARS}
                actionItems={actionItems}
                loadingActionItems={loadingActionItems}
                showAlways={true}
              />
              
                      {questionnaire.questions[1] && (
                <PillarQuestion
                  question={questionnaire.questions[1]}
                  selectedRating={responses[1]}
                  onRatingChange={(rating) => handleResponseChange(1, rating)}
                  show={vibeCheckAnswer && (vibeCheckAnswer === 'YES' || selectedPillar)}
                />
              )}
            </>
          ) : (
            /* Old Format */
            questionnaire.questions.map((question, index) => (
              <QuestionCard
                key={index}
                question={question}
                questionNumber={index + 1}
                totalQuestions={questionnaire.questions.length}
                selectedRating={responses[index]}
                onRatingChange={(rating) => handleResponseChange(index, rating)}
              />
            ))
          )}
        </div>

        {/* Submit Button */}
        <div className="mt-16 pb-8 flex flex-col items-center">
          <button
            onClick={handleSubmit}
            disabled={getCompletionPercentage() < 100 || submitting}
            className="questionnaire-button questionnaire-button-accent"
            style={{ 
              padding: '16px 48px',
              fontSize: '18px',
              fontWeight: '600',
              borderRadius: '12px',
              minWidth: '300px',
              height: '56px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: getCompletionPercentage() < 100 ? 0.5 : 1
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Responses'}
          </button>
          
          {getCompletionPercentage() < 100 && (
            <p className="text-base mt-6" style={{ color: '#6b7280' }}>
              Please answer all questions to submit
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const QuestionCard = ({ 
  question, 
  questionNumber, 
  totalQuestions, 
  selectedRating, 
  onRatingChange 
}) => {
  const ratings = [
    { value: 1, label: 'Never' },
    { value: 2, label: 'Rarely' },
    { value: 3, label: 'Sometimes' },
    { value: 4, label: 'Often' },
    { value: 5, label: 'Always' }
  ];

  return (
    <div className="stats-card">
      <div className="mb-8">
        <div className="flex items-start justify-between mb-6">
          <span className="text-sm font-medium px-3 py-1 rounded-full" style={{ 
            color: '#6b7280',
            background: '#f3f4f6'
          }}>
            Question {questionNumber} of {totalQuestions}
          </span>
          {selectedRating && (
            <div className="flex items-center px-3 py-1 rounded-full" style={{ background: '#d1fae5' }}>
              <CheckCircle className="h-4 w-4 mr-2" style={{ color: '#10b981' }} />
              <span className="text-sm font-medium" style={{ color: '#059669' }}>
                Answered
              </span>
            </div>
          )}
        </div>
        
        <h3 className="text-xl font-semibold leading-relaxed mb-4" style={{ color: '#111827' }}>
          {question.text}
        </h3>
        
        <p className="text-base mb-6" style={{ color: '#6b7280' }}>
          {question.rating_scale}
        </p>
      </div>

      <div className="space-y-6">
        {ratings.map((rating) => {
          const isSelected = selectedRating === rating.value;
          return (
            <button
              key={rating.value}
              onClick={() => onRatingChange(rating.value)}
              className="w-full flex items-center py-4 transition-all duration-200 text-left"
              style={{
                background: 'transparent',
                border: 'none',
                transform: isSelected ? 'translateX(8px)' : 'translateX(0)'
              }}
            >
              <div className="flex items-center flex-1">
                <span className="font-semibold text-xl mr-6" style={{
                  color: isSelected ? '#6366f1' : '#111827'
                }}>
                  {rating.label}
                </span>
                
                <div className="flex">
                  {[...Array(rating.value)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 mr-1"
                      style={{
                        color: isSelected ? '#6366f1' : '#d1d5db',
                        fill: isSelected ? '#6366f1' : '#d1d5db'
                      }}
                    />
                  ))}
                </div>
              </div>
              
              {isSelected && (
                <CheckCircle 
                  className="h-6 w-6 ml-4" 
                  style={{ color: '#6366f1' }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Vibe Check Question Component - Mobile-First Design
const VibeCheckQuestion = ({ 
  question, 
  vibeCheckAnswer, 
  onAnswerChange, 
  selectedPillar, 
  onPillarSelect, 
  pillars,
  showAlways = false
}) => {
  return (
    <div style={{ 
      background: 'white',
      borderRadius: '20px',
      padding: '24px',
      marginBottom: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: vibeCheckAnswer ? '2px solid #10b981' : '2px solid #e5e7eb'
    }}>
      {/* Progress Badge */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <span style={{ 
          fontSize: '13px',
          fontWeight: '600',
          color: '#6b7280',
          background: '#f3f4f6',
          padding: '6px 14px',
          borderRadius: '20px',
          letterSpacing: '0.5px'
        }}>
          QUESTION 1 OF 2
        </span>
        {vibeCheckAnswer && (vibeCheckAnswer === 'YES' || selectedPillar) && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '6px',
            background: '#d1fae5',
            padding: '6px 12px',
            borderRadius: '20px'
          }}>
            <CheckCircle size={16} color="#10b981" strokeWidth={2.5} />
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>
              Complete
            </span>
          </div>
        )}
      </div>
      
      {/* Question Text */}
      <h3 style={{ 
        fontSize: '22px',
        fontWeight: '700',
        color: '#111827',
        lineHeight: '1.4',
        marginBottom: '28px'
      }}>
        {question.text}
      </h3>

      {/* Yes/No Buttons - Large Touch Targets */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <button
          onClick={() => onAnswerChange('YES')}
          style={{
            minHeight: '100px',
            background: vibeCheckAnswer === 'YES' 
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : '#f9fafb',
            color: vibeCheckAnswer === 'YES' ? 'white' : '#374151',
            border: vibeCheckAnswer === 'YES' ? 'none' : '3px solid #e5e7eb',
            borderRadius: '16px',
            fontSize: '20px',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: vibeCheckAnswer === 'YES' ? '0 8px 24px rgba(16, 185, 129, 0.3)' : 'none',
            transform: vibeCheckAnswer === 'YES' ? 'scale(1.02)' : 'scale(1)'
          }}
        >
          <ThumbsUp size={32} strokeWidth={2.5} />
          <span>Yes</span>
        </button>
        
        <button
          onClick={() => onAnswerChange('NO')}
          style={{
            minHeight: '100px',
            background: vibeCheckAnswer === 'NO' 
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : '#f9fafb',
            color: vibeCheckAnswer === 'NO' ? 'white' : '#374151',
            border: vibeCheckAnswer === 'NO' ? 'none' : '3px solid #e5e7eb',
            borderRadius: '16px',
            fontSize: '20px',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: vibeCheckAnswer === 'NO' ? '0 8px 24px rgba(239, 68, 68, 0.3)' : 'none',
            transform: vibeCheckAnswer === 'NO' ? 'scale(1.02)' : 'scale(1)'
          }}
        >
          <ThumbsDown size={32} strokeWidth={2.5} />
          <span>No</span>
        </button>
      </div>

      {/* Follow-up if No - Button Grid - Always visible when NO is selected */}
      {vibeCheckAnswer === 'NO' && (
        <div style={{ 
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderRadius: '16px',
          padding: '20px',
          marginTop: '20px',
          border: selectedPillar ? '3px solid #10b981' : '2px solid #f59e0b'
        }}>
          <h4 style={{ 
            fontSize: '18px',
            fontWeight: '700',
            color: '#92400e',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            Where do you need the most help?
          </h4>
          
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px',
            marginBottom: selectedPillar ? '16px' : '0'
          }}>
            {pillars.map((pillar) => {
              const isSelected = selectedPillar === pillar;
              const pillarShortName = pillar.replace('We ', '');
              
              return (
                <button
                  key={pillar}
                  onClick={() => onPillarSelect(pillar)}
                  style={{
                    minHeight: '80px',
                    background: isSelected 
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      : 'white',
                    color: isSelected ? 'white' : '#92400e',
                    border: isSelected ? 'none' : '2px solid #f59e0b',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '12px',
                    lineHeight: '1.3',
                    boxShadow: isSelected ? '0 4px 12px rgba(245, 158, 11, 0.4)' : 'none',
                    transform: isSelected ? 'scale(1.05)' : 'scale(1)'
                  }}
                >
                  {pillarShortName}
                </button>
              );
            })}
          </div>
          
          {selectedPillar && (
            <div style={{ 
              background: 'white',
              borderRadius: '8px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <p style={{ 
                fontSize: '13px',
                color: '#059669',
                margin: '0',
                lineHeight: '1.5',
                fontWeight: '600'
              }}>
                ✓ You selected <strong>{selectedPillar}</strong> - You'll receive personalized action items after submitting
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Pillar Question Component - Mobile-First Design
const PillarQuestion = ({ question, selectedRating, onRatingChange, show = true }) => {
  const ratings = [
    { value: 1, label: 'Never', emoji: '😔', color: '#ef4444' },
    { value: 2, label: 'Rarely', emoji: '😕', color: '#f59e0b' },
    { value: 3, label: 'Sometimes', emoji: '😐', color: '#eab308' },
    { value: 4, label: 'Often', emoji: '🙂', color: '#84cc16' },
    { value: 5, label: 'Always', emoji: '😊', color: '#10b981' }
  ];

  if (!show) return null;

  return (
    <div style={{ 
      background: 'white',
      borderRadius: '20px',
      padding: '24px',
      marginBottom: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    }}>
      {/* Progress Badge */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <span style={{ 
          fontSize: '13px',
          fontWeight: '600',
          color: '#6b7280',
          background: '#f3f4f6',
          padding: '6px 14px',
          borderRadius: '20px',
          letterSpacing: '0.5px'
        }}>
          QUESTION 2 OF 2
        </span>
        {selectedRating && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '6px',
            background: '#d1fae5',
            padding: '6px 12px',
            borderRadius: '20px'
          }}>
            <CheckCircle size={16} color="#10b981" strokeWidth={2.5} />
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>
              Done
            </span>
          </div>
        )}
      </div>
      
      {/* Question Text */}
      <h3 style={{ 
        fontSize: '22px',
        fontWeight: '700',
        color: '#111827',
        lineHeight: '1.4',
        marginBottom: '12px'
      }}>
        {question.text}
      </h3>
      
      <p style={{ 
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '28px',
        lineHeight: '1.5'
      }}>
        Tap to select how often this applies to you
      </p>

      {/* Rating Buttons - Large Touch Targets */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {ratings.map((rating) => {
          const isSelected = selectedRating === rating.value;
          return (
            <button
              key={rating.value}
              onClick={() => onRatingChange(rating.value)}
              style={{
                width: '100%',
                minHeight: '72px',
                background: isSelected 
                  ? `linear-gradient(135deg, ${rating.color} 0%, ${rating.color}dd 100%)`
                  : '#f9fafb',
                color: isSelected ? 'white' : '#374151',
                border: isSelected ? 'none' : '2px solid #e5e7eb',
                borderRadius: '16px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 20px',
                boxShadow: isSelected ? `0 8px 24px ${rating.color}40` : 'none',
                transform: isSelected ? 'scale(1.02)' : 'scale(1)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '32px' }}>{rating.emoji}</span>
                <span>{rating.label}</span>
              </div>
              
              <div style={{ display: 'flex', gap: '4px' }}>
                {[...Array(rating.value)].map((_, i) => (
                  <Star
                    key={i}
                    size={20}
                    style={{
                      color: isSelected ? 'white' : rating.color,
                      fill: isSelected ? 'white' : rating.color
                    }}
                    strokeWidth={2}
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default QuestionnaireForm;